// Cash Funding — intercompany funding receipt that posts against a Cash Request.
// Mirrors Finance.API CashFundingDto / CashFundingInsert.
import { FormControl } from '@angular/forms';
import { Currency } from '../bank-account/bank-account';

export enum CashFundingStatus {
  Draft = 1,
  Posted = 2,
  Cancelled = 3,
}

/** Mirrors Global.Models S3ObjectDto (funding document attachments). */
export interface S3Object {
  name: string;
  presignedUrl: string;
}

export interface CashFunding {
  id?: string | null;
  number?: string;
  status: CashFundingStatus;
  fundingDate: string;
  funderReference: string;
  funderName?: string;
  financialCompanyReference?: string;
  cashRequestReference: string;
  cashRequestNumber?: string;
  amount: number;
  currency: Currency;
  transferFee: number;
  securityTax: number;
  netCashReceived?: number;
  bankAccountReference?: string | null;
  bankTransactionNumber?: string | null;
  bankingReference?: string | null;
  /** #5032 — the id was missing here while the API's CashFundingDto has always returned it, so the page
   *  could show the entry's number but never fetch the entry itself. */
  journalEntryReference?: string | null;
  journalEntryDocumentNumber?: string | null;
  attachments?: S3Object[];
  createdBy?: string;
  createdDate?: string;
}

/** Create-draft payload — funder/FinancialCompany/Number/status are derived API-side. */
export interface CashFundingInsert {
  funderReference?: string | null;
  cashRequestReference: string;
  fundingDate: string;
  bankAccountReference?: string | null;
  amount: number;
  currency: Currency;
  transferFee: number;
  securityTax: number;
  /** Bank confirmation / wire / transfer reference, captured at create so a draft keeps it. */
  bankingReference?: string | null;
}

/** Editable fields of a Draft funding (destination bank, amount, fees, date, bank reference). */
export interface CashFundingUpdate {
  fundingDate: string;
  bankAccountReference?: string | null;
  amount: number;
  transferFee: number;
  securityTax: number;
  bankingReference?: string | null;
}

/** Body sent when posting — the real bank transaction number (same shape as ApPayment). */
export interface CashFundingPostConfirm {
  bankTransactionNumber?: string | null;
}

/** An Approved Cash Request available to fund (GET /cash-fundings/fundable-requests). */
export interface FundableCashRequest {
  id: string;
  number: string;
  weekStart?: string;
  weekEnd?: string;
  totalAmount: number;
  financialCompanyReference?: string;
}

/**
 * Strict reactive form for the unified Cash Funding details/new screen. `cashRequest` is only used when
 * creating (the request is fixed on an existing funding); the funder + currency are derived from it.
 */
export interface CashFundingForm {
  cashRequest: FormControl<string | null>;
  fundingDate: FormControl<Date | null>;
  bankAccount: FormControl<string | null>;
  amount: FormControl<number | null>;
  bankReference: FormControl<string | null>;
  bankingFee: FormControl<number | null>;
  securityFee: FormControl<number | null>;
}
